import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';
import { formUtils } from '@insurancemenu/imenu-components-lib';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import {
  CartBrowsingCriteria,
  CartItem,
  Portfolio,
  BrowsingInfo,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { Cart } from 'types/cartServiceV2';
import { INITIAL_BROWSING_CRITERIA } from 'store/domain/cartStoreV2';
import { FetchResult } from '@apollo/client';
import { requestBCForm, INITIAL_FORM_OBJECT } from 'api/operations/formService';

export default class PortfolioConfigModalStore {
  rootStore: RootStore;
  form: FormObjectFromJSON;
  dynamicForm: null | formUtils.DynamicFormController;

  loading = false;
  showConfigurePlanModal = false;

  portfolioId = '';
  portfolioName = '';
  productLineId: ProductLineId = 'GroupDental';
  cartId = '';
  selectedIndex = 0;
  portfolioBrowsingCriteria: CartBrowsingCriteria;

  setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  async openConfigurePlanModal(
    portfolio: Portfolio,
    cartId: string,
    effectiveDate: string,
    cartItems: CartItem[],
  ): Promise<void> {
    this.showConfigurePlanModal = true;
    this.setLoading(true);
    this.portfolioId = portfolio.id;
    this.cartId = cartId;
    this.portfolioName = portfolio.name;
    this.productLineId = portfolio.productLineId;

    const cartItem = cartItems.find((item) => item.portfolioId === this.portfolioId);
    this.selectedIndex = cartItem?.selectedIndex || 0;
    this.dynamicForm = null;
    const res = await requestBCForm(this.portfolioId, effectiveDate);
    runInAction(() => {
      this.form = res.data.getBCFormsByPortfolioId[0];
      const bc = cartItem?.browsingCriteria[cartItem.selectedIndex];
      if (bc) {
        this.portfolioBrowsingCriteria = bc;
        this.createFormController(bc[this.productLineId]);
      }
      this.setLoading(false);
    });
  }

  closeConfigurePlanModal(): void {
    this.showConfigurePlanModal = false;
  }

  createFormController(answers: BrowsingInfo | undefined): void {
    this.setLoading(true);
    this.dynamicForm = new formUtils.DynamicFormController(this.form, this.rootStore.userStore.userRoles);
    if (answers) {
      this.dynamicForm.populateFields(answers);
    }
    this.setLoading(false);
  }

  // eslint-disable-next-line class-methods-use-this
  async saveAnswers(): Promise<FetchResult<{ addPortfolioConfigToCart: Cart }>> {
    const browsingCriteriaAnswersFromForm = this.portfolioBrowsingCriteria[this.productLineId] || {};

    Object.entries(this.dynamicForm!.getCanonicalAnswers()).forEach(([key, value]) => {
      browsingCriteriaAnswersFromForm[key] = value?.toString() || '';
    });
    this.portfolioBrowsingCriteria[this.productLineId] = browsingCriteriaAnswersFromForm;
    return this.rootStore.cartStoreV2.addPortfolioToCart({
      cartId: this.cartId,
      portfolioId: this.portfolioId,
      portfolioName: this.portfolioName,
      browsingCriteria: this.portfolioBrowsingCriteria,
      productLineId: this.productLineId,
      bcIndex: this.selectedIndex,
    });
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.dynamicForm = null;
    this.form = INITIAL_FORM_OBJECT;
    this.portfolioBrowsingCriteria = INITIAL_BROWSING_CRITERIA;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
